import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import Container from './container'
// import DraggableChild from './child-component'
// import { generateUniqueId } from '../../Global'
// import Buttons from '../../components/buttons/buttons'

interface DroppableAreaProps {
  column?: 'left' | 'right'
  selectedContainer: string | null;
  setSelectedContainer: (id: string | null) => void;
  layout: number[] | null;
  handleChildSelect?: any
  onChildrenCountUpdate?: any
  setChildrenLengths?: any
  setChildSelects?: any
  formSettings?: any
  setChangeLabel?: any
  changedLabel?: any
  setSelectChilds?: any
  containersData: { id: string, rowData: { id: string; childData: { id: string; type: string; label: string; isInfo: boolean; isMandatory: boolean }[]; isDataRow?: boolean }[] }[],
  setContainersData?: any
  containerId: any
  clickedId?: any
  containerClassName?: string
  onNewUpdate?: any,
  onDelete?: (id: any) => void,
  selectedRowId?: string | undefined,
  selectedChildId: string | null,
  setSelectedChildId: React.Dispatch<React.SetStateAction<string | null>>
  setSelectedWholeRowId: React.Dispatch<React.SetStateAction<string | null>>
  selectedWholeRowId: string | null
  setSelectedType: React.Dispatch<React.SetStateAction<string | null>>
  setSelectedSubChild: React.Dispatch<React.SetStateAction<string | null>>
  setSelectedLayout: React.Dispatch<any>
  selectedLayout: any
}

interface SectionWrapperProps {
  id: string
  index: number
  moveSection: (dragIndex: number, hoverIndex: number) => void
  children: React.ReactNode
}

const SectionWrapper: React.FC<SectionWrapperProps> = ({ id, index, moveSection, children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)

  const [{ isDraggingSection }, drag, preview] = useDrag(() => ({
    type: 'SECTION',
    item: () => ({ id, index }),
    collect: (monitor) => ({ isDraggingSection: monitor.isDragging() }),
  }), [id, index])

  const [, drop] = useDrop(() => ({
    accept: 'SECTION',
    hover: (item: { id: string; index: number }) => {
      if (item.index === index) return
      moveSection(item.index, index)
      item.index = index
    },
  }), [index, moveSection])

  useEffect(() => {
    if (wrapperRef.current) { preview(wrapperRef.current); drop(wrapperRef.current) }
    if (handleRef.current) drag(handleRef.current)
  }, [drag, drop, preview])

  return (
    <div ref={wrapperRef} style={{ opacity: isDraggingSection ? 0.4 : 1, position: 'relative' }}>
      {/* <div
        ref={handleRef}
        title="Drag to reorder section"
        style={{ cursor: 'grab', padding: '0 4px', opacity: 0.35, userSelect: 'none', display: 'inline-flex', alignItems: 'center', fontSize: 14 }}
      >⠿</div> */}
      {children}
    </div>
  )
}

const DroppableArea: React.FC<DroppableAreaProps> = ({
  column: _column = 'left',
  selectedContainer,
  setSelectedContainer,
  layout,
  containerClassName: _containerClassName,
  onNewUpdate: _onNewUpdate,
  onChildrenCountUpdate,
  setChildrenLengths,
  setChildSelects,
  formSettings,
  changedLabel,
  setSelectChilds,
  containersData,
  setContainersData,
  containerId,
  clickedId,
  onDelete: _onDelete,
  selectedRowId,
  selectedChildId,
  setSelectedChildId,
  setSelectedWholeRowId,
  selectedWholeRowId,
  setSelectedSubChild,
  setSelectedType,
  selectedLayout,
  setSelectedLayout
}) => {
  let childCounter = 0
  const generateUniqueId = (prefix: string) => `${prefix}-${Date.now()}-${childCounter++}`

  const moveSection = useCallback((dragIndex: number, hoverIndex: number) => {
    setContainersData((prev: any) => {
      const updated = [...prev]
      const [moved] = updated.splice(dragIndex, 1)
      updated.splice(hoverIndex, 0, moved)
      return updated
    })
  }, [setContainersData])

  const [, setSelectChild] = useState<{
    id: string | null;
    type: string | null;
    label: string | null;
    isInfo: boolean | null;
    isMandatory: boolean | null
  }>({ id: null, type: null, label: null, isInfo: null, isMandatory: null })

  const [{ isOver }, dropRef] = useDrop(() => ({
    accept: 'CONTAINER',
    drop: () => {
      const newContainer = {
        id: generateUniqueId('container'),
        rowData: [
          { id: generateUniqueId('row-data'), childData: [], isDataRow: true },
          { id: generateUniqueId('row'), childData: [], isDataRow: false },
        ],
      }
      setContainersData((prev: any) => [...prev, newContainer])
      onChildrenCountUpdate?.(newContainer.id, 0)
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }))

  const getChildrenLengths = (data: any) => {
    if (data?.length > 0) {
      setChildrenLengths(data)
    }
  }

  const getChildSelects = (selectedchild: any, containerId: any, rowId: any, childId?: any) => {
    setChildSelects(selectedchild, containerId, rowId, childId)
  }

  useEffect(() => {

    // Ensure a default container is present and selected

    if (containersData.length === 0) {

      const defaultContainer = {
        id: generateUniqueId('container'),
        rowData: [
          { id: generateUniqueId('row-data'), childData: [], isDataRow: true },
          { id: generateUniqueId('row'), childData: [], isDataRow: false },
        ],
      }

      setContainersData([defaultContainer])

      setSelectedContainer(defaultContainer.id)

    }

  }, [containersData, setContainersData, setSelectedContainer])

  const handleChildDrop = (
    containerId: string,
    rowId: string,
    children: { id?: string; type?: string; label?: string; isInfo?: boolean; isMandatory?: boolean }
  ) => {
    const uniqueChildId = generateUniqueId('child')

    setContainersData((prevContainers: any) =>
      prevContainers.map((container: any) => {
        if (container.id === containerId) {
          return {
            ...container,
            rowData: container.rowData.map((existingRow: any) =>
              existingRow.id === rowId
                ? {
                  ...existingRow,
                  childData: [
                    ...existingRow.childData,
                    { ...children, id: uniqueChildId, internalName: '' },
                  ],
                }
                : existingRow
            ),
          }
        }
        return container
      })
    )
  }
  // const handleChildSelect = (childId: string | null, childType: string | null, childLabel: string | null, childInfo: boolean | null, childMandatory: boolean | null) => {
  //   setSelectChild({ id: childId, type: childType, label: childLabel, isInfo: childInfo, isMandatory: childMandatory })
  // }
  const handleChildSelect = (
    childId: string | null,
    childType: string | null,
    childLabel: string | null,
    childInfo: boolean | null,
    childMandatory: boolean | null
  ) => {
    // Update the child selection
    setSelectChild({ id: childId, type: childType, label: childLabel, isInfo: childInfo, isMandatory: childMandatory })
    setSelectedChildId(childId)

    if (childId !== null) {
      // Deselect parent container when child is selected
      setSelectedContainer(null)
    }
  }

  return (<>
    <div
      ref={dropRef}
      className={`min-h-100 whitebg border-radius-2 border-2 p-3 pb-2 ${isOver ? 'bgcolor-primary-light' : ''}`}
    >
      {containersData.length === 0 && (
        <p style={{ color: '#999', textAlign: 'center' }}>Drop containers here</p>
      )}
      {containersData.map(({ id, rowData }, index) => (
        <SectionWrapper key={id} id={id} index={index} moveSection={moveSection}>
        <Container
          dynamicContainerId={id}
          isSelected={id === selectedContainer}
          // onSelect={() => {
          //   setSelectedContainer(selectedContainer === id ? null : id)
          // }}
          onSelect={() => {
            const newSelection = selectedContainer === id ? null : id
            setSelectedContainer(newSelection)
            // Deselect any selected child if a container is selected
            if (newSelection !== null) {
              setSelectChild({ id: null, type: null, label: null, isInfo: null, isMandatory: null })
              setSelectedChildId(null)
            }
          }}
          layout={layout}
          onChildDrop={handleChildDrop}
          onChildSelect={handleChildSelect} getChildrenLengths={getChildrenLengths} getChildSelects={getChildSelects}
          formSettings={formSettings} changedLabel={changedLabel} clickedId={clickedId}
          // rowClassName={containerClassName}
          setSelectChilds={setSelectChilds}
          rowData={rowData}
          setContainersData={setContainersData}
          setSelectedContainer={setSelectedContainer}
          containerId={containerId}
          selectedRowId={selectedRowId}
          selectedChildId={selectedChildId}
          setSelectedChildId={setSelectedChildId}
          setSelectedWholeRowId={setSelectedWholeRowId}
          selectedWholeRowId={selectedWholeRowId}
          containersData={containersData}
          setSelectedSubChild={setSelectedSubChild}
          setSelectedType={setSelectedType}
          selectedLayout={selectedLayout}
          setSelectedLayout={setSelectedLayout}
        />
        </SectionWrapper>
      ))}

    </div>
  </>
  )
}

export default DroppableArea
